import { CSSProperties, MutableRefObject, useEffect, useRef, useState } from 'react';
import anime from 'animejs';

interface UseAnimatedPathOptions {
  completed?: boolean;
  enabled?: boolean;
  delay?: number;
  disableLoop?: boolean;
  opacity?: boolean;
}

export interface UseAnimatedPathResult<T = SVGElement> {
  ref: MutableRefObject<T | null>;
  style: CSSProperties;
  className: string;
}

export default function useAnimatedPath<T = SVGPathElement>({
  completed,
  enabled,
  disableLoop,
  delay = 500,
  opacity = false,
}: UseAnimatedPathOptions): UseAnimatedPathResult<T> {
  const ref = useRef<T | null>(null);
  const [style, setStyle] = useState<CSSProperties>({});
  const [isEnabled, setIsEnabled] = useState(enabled);
  useEffect(() => {
    if (isEnabled) {
      setStyle({ opacity: 1 });
    }
  }, [isEnabled, setStyle]);

  useEffect(() => {
    const targets = ref.current;
    if (targets) {
      if (completed || !isEnabled) return;
      const config = opacity ? { opacity: [0, 1] } : { strokeDashoffset: [anime.setDashoffset, 0] };
      anime({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        targets,
        ...config,
        easing: 'easeInOutSine',
        duration: 500,
        delay,
      });
      // eslint-disable-next-line consistent-return
      return () => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        anime.remove(targets);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        targets.style.removeProperty('stroke-dashoffset');
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        targets.style.removeProperty('stroke-dasharray');
      };
    }
  }, [ref, completed, delay, isEnabled, opacity]);
  useEffect(() => {
    setIsEnabled((value) => (disableLoop ? value || enabled : enabled));
  }, [setIsEnabled, enabled, disableLoop]);
  return {
    ref,
    style,
    className: 'animated-path',
  };
}
